'use client'

import { forwardRef, useContext } from 'react'
import NextLink from 'next/link'

import { Link as MuiLink } from '@mui/material'
import type { LinkProps } from '@/types/components'
import LocaleContext from '@/providers/context/LocaleContext'
import { localesList } from '@/utils/locale'

export const isAbsoluteUrl = (url: string) => /^https?:\/\//i.test(url)

export const isRelativeUrl = (url: string) => !isAbsoluteUrl(url)

export const getPathFromHref = (href: string) => (isAbsoluteUrl(href) ? new URL(href).pathname : href)

const hasLocalePrefix = (href: string) => {
  const first = href.split('/').filter(Boolean)[0]
  return !!first && localesList.includes(first)
}

const shouldSkipLocalePrefix = (href: string) =>
  isAbsoluteUrl(href) ||
  href.startsWith('#') ||
  href.startsWith('?') ||
  href.startsWith('mailto:') ||
  href.startsWith('tel:') ||
  href.startsWith('//')

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, href, children, isImplicit, isFullUrl, target, title, className, rel, ...rest }, ref) => {
    const { localePrefix } = useContext(LocaleContext)
    const rawUrl = href ? href : to || null

    // Prepend the current locale to internal links
    let url = rawUrl
    if (
      !isFullUrl &&
      typeof rawUrl === 'string' &&
      rawUrl.startsWith('/') &&
      !shouldSkipLocalePrefix(rawUrl) &&
      !hasLocalePrefix(rawUrl) &&
      localePrefix
    ) {
      url = `${localePrefix}${rawUrl}`
    }

    const linkTitle = title || rest['aria-label'] || undefined

    delete rest.anchorClassName
    delete rest.showLabel

    const resolvedTarget = target ?? (typeof url === 'string' && isAbsoluteUrl(url) ? '_blank' : undefined)
    const resolvedRel = rel ?? (resolvedTarget === '_blank' ? 'noopener noreferrer' : undefined)

    return (
      <MuiLink
        className={className}
        target={resolvedTarget}
        rel={resolvedRel}
        href={url}
        component={NextLink}
        role={!!url ? 'link' : 'button'}
        title={linkTitle}
        ref={ref}
        {...rest}>
        {children}
      </MuiLink>
    )
  }
)

export default Link
